import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { UserStore } from '../store/userStore'
import Card from '../components/Card'
import { ERROR_DICT, errorDict } from '../utils/errorDict'
import TextField from '../components/TextField'
import RadioGroup from '../components/RadioGroup'
import {
  PHONE_VALIDATION_PATTERN,
  SNILS_VALIDATION_PATTERN,
  NAME_VALIDATION_PATTERN
} from '../constants'
import Btn from '../components/Btn'
import { useAxios } from '../hooks/useAxios'
import { updateUser } from '../api/updateUser'
import { type IUser } from '../server/user/get'
import { toast } from 'react-toastify'
import CheckboxSingle from '../components/CheckboxSingle'
import SocialsConnection from '../components/SocialsConnection'
import Toggle from '../components/Toggle'

function ProfileEditMainPage(): React.ReactElement {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch
  } = useForm({
    defaultValues: UserStore.data
  })

  const { loading, fire } = useAxios()
  const isForeignCitizen = watch('foreignCitizen')

  const onSubmit = async (data: IUser): Promise<void> => {
    const { response, error } = await fire(updateUser(data))
    if (error) {
      toast.error(error.message)
    } else {
      toast.success('Изменения успешно сохранены')
      const token = response?.data ?? ''

      if (typeof token === 'string') {
        localStorage.setItem('token', token)
        UserStore.updateUserData()
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card>
        <header className={'card-head text-h3'}>Личная информация</header>

        <div className={'card-content'}>
          <div className={'row row-gap-20 row-mobile-none'}>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: NAME_VALIDATION_PATTERN,
                  message: ''
                }
              }}
              hasError={!!errors.lastName}
              errorText={errorDict(errors.lastName)}
              name="lastName"
              label="Фамилия"
              required
            ></TextField>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                required: true,
                pattern: {
                  value: NAME_VALIDATION_PATTERN,
                  message: ''
                }
              }}
              hasError={!!errors.name}
              errorText={errorDict(errors.name)}
              name="name"
              label="Имя"
              required
            ></TextField>
          </div>
          <div className={'row row-gap-20 row-mobile-none'}>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                pattern: {
                  value: NAME_VALIDATION_PATTERN,
                  message: ''
                }
              }}
              hasError={!!errors.secondName}
              errorText={errorDict(errors.secondName)}
              name="secondName"
              label="Отчество"
            ></TextField>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{ required: true }}
              hasError={!!errors.birthday}
              errorText={errorDict(errors.birthday)}
              name="birthday"
              type="date"
              label="Дата рождения"
              required
            ></TextField>
          </div>
          <RadioGroup
            name={'gender'}
            control={control}
            items={['Мужской', 'Женский']}
            rules={{ required: true }}
            hasError={!!errors.gender}
            errorText={errorDict(errors.gender)}
            required
          ></RadioGroup>
          <CheckboxSingle hideDetails name={'foreignCitizen'} control={control}>
            Не гражданин РФ
          </CheckboxSingle>
        </div>
      </Card>
      <Card className={'mt-md-5 mt-sm-4'}>
        <header className={'card-head text-h3'}>Контакты</header>

        <div className={'card-content'}>
          <div className={'card-wrapper card-wrapper-mobile'}>
            <div className={'column row-gap-20 _full-width'}>
              <TextField
                className={'u-flexGrow1 _hide-details'}
                mask={'+7 (999) 999-99-99'}
                control={control}
                rules={{
                  required: true,
                  pattern: { value: PHONE_VALIDATION_PATTERN, message: '' }
                }}
                hasError={!!errors.phone}
                errorText={
                  errors.phone?.type === 'pattern'
                    ? ERROR_DICT.phone
                    : errorDict(errors.phone)
                }
                name="phone"
                type="tel"
                label={'Номер телефона'}
                required
              ></TextField>
              <Controller
                name="showPhone"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <Toggle
                    defaultChecked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    text="Показывать в профиле"
                  />
                )}
              />
            </div>

            <div className={'column row-gap-20 _full-width'}>
              <TextField
                className={'u-flexGrow1 _hide-details'}
                control={control}
                name="email"
                type="email"
                label={'Электронная почта'}
                required
              ></TextField>
              <Controller
                name="showEmail"
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <Toggle
                    defaultChecked={field.value}
                    onChange={(checked) => field.onChange(checked)}
                    text="Показывать в профиле"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Card>
      <Card className={'mt-md-5 mt-sm-4'}>
        <header className={'card-head text-h3'}>Документы</header>

        <div className={'card-content'}>
          <TextField
            key={'snils'}
            mask={'999-999-999 99'}
            control={control}
            rules={{
              validate: (value) => {
                if (isForeignCitizen) return true

                return SNILS_VALIDATION_PATTERN.test(value)
              }
            }}
            hasError={!!errors.snils}
            errorText={ERROR_DICT.snils}
            name="snils"
            type="text"
            label="CНИЛС"
            required={!isForeignCitizen}
            disabled={isForeignCitizen}
          ></TextField>
        </div>
      </Card>

      <Card className={'mt-md-5 mt-sm-4'}>
        <header className={'card-head d-block'}>
          <p className={'text-h3'}>Социальные сети</p>
          <p className={'mt-md-3 mt-sm-2'}>
            Подключайте соцсети для быстрой авторизации без логина и пароля
          </p>
        </header>

        <div className={'card-content'}>
          <SocialsConnection></SocialsConnection>
        </div>
      </Card>

      <Btn
        className={'mt-md-8 mt-sm-6'}
        disabled={!isDirty || loading}
        loading={loading}
        type={'submit'}
      >
        Сохранить изменения
      </Btn>
    </form>
  )
}

export default ProfileEditMainPage
